import { PartnerParams } from '../../helpers/partnerParamsHelper';
import { OutputFormData } from '@ikea-customer-self-serve/contact-form';
import { PriceProps } from '@ingka/price';
import { ServiceLocation } from '../../helpers';

interface ExtendedOutputFormData extends Omit<
  OutputFormData,
  'additionalRecipient'
> {
  isMobileChanged?: boolean;
}

export interface Payload {
  appointmentId?: string;
  plannerId?: string;
  projectId?: string;
  partnerParams?: PartnerParams;
  customer: ExtendedOutputFormData;
  additionalRecipient?: OutputFormData['additionalRecipient'];
  eventLocation: string;
}

export interface AppointmentResponse {
  orderNo: string;
  payOrderLink?: string;
  questionsToken?: string;
  payOrderLinkEnabled: boolean;
}

export interface PayOrderLinkResponse {
  link: string;
}

export type LocationType =
  | 'STORE'
  | 'HOME'
  | 'STORE_REMOTE'
  | 'REMOTE'
  | 'STORE_HOME';

export interface PriceParts extends Partial<PriceProps> {
  integerValue: string;
}

export interface FormattedPrice {
  mainPrice: PriceParts;
  secondaryPrice?: PriceParts;
}

export interface Savings {
  amount: number;
  validFromDateUTC: string;
  validToDateUTC: string;
  id: string;
}

export type PriceType = 'Business' | 'Family' | 'Private';

export interface PriceWithSavings {
  price: {
    currencyCode: string;
    priceExclTax?: number;
    priceExclTaxWithSavings?: number;
    priceInclTax?: number;
    priceInclTaxWithSavings?: number;
    priceType: PriceType;
    totalDiscount?: number;
  };
  savings?: Savings[];
}
export interface ReplacementServiceResponse {
  isExternalService: boolean;
  locationType: LocationType;
  operation: string;
  prices: PriceWithSavings[];
  serviceCode: string;
  serviceProductId: string;
}
export interface ReplacementService {
  customerPrice: CustomerPrice;
  displayPrice: DisplayPrice;
  isExternalService: boolean;
  location: ServiceLocation;
  operation: string;
  serviceCode: string;
  serviceId: string;
}

export type AvailableServicesInZipResponse = {
  isExternalService: boolean;
  locationType: LocationType;
  prices: PriceWithSavings[];
  replacementService?: ReplacementServiceResponse;
  serviceCode: string;
  serviceProductId: string;
};

export interface AvailableTimeslotsResponse {
  id: string;
  from: string;
  to: string;
  externalId: string;
}

export interface TimeSlot {
  id: string;
  appointmentId: string;
  dateFrom: string;
  dateTo: string;
  from: string;
  to: string;
  isReplacementService?: boolean;
}

export type SlotStatus = 'pending' | 'completed' | 'error';

export interface Slot {
  date: string;
  timeSlots: TimeSlot[];
  status: SlotStatus;
}

export type PriceOfferType = 'family' | 'ibn' | 'tro';

export interface CustomerPrice {
  price: number;
  priceFormatted: FormattedPrice;
  priceOfferType?: PriceOfferType;
  totalSavings?: number;
}

export interface DisplayPrice {
  currencyCode: string;
  exclSavings: number;
  exclSavingsFormatted: FormattedPrice;
  inclSavings: number;
  inclSavingsFormatted: FormattedPrice;
  priceOfferType?: PriceOfferType;
  totalSavings: number;
  validFromDateUTC?: string;
  validToDateUTC?: string;
}

export interface AvailableService {
  customerPrice: CustomerPrice;
  displayPrice: DisplayPrice;
  location: ServiceLocation;
  isExternalService: boolean;
  replacementService?: ReplacementService;
  serviceCode: string;
  serviceId: string;
}

export interface ServicesResponse {
  services: AvailableService[];
}

export interface AvailableStore {
  value: string;
  text: string;
  street: string;
  zipCode: string;
  city: string;
}

export interface StoresResponse {
  stores: AvailableStore[];
}

export interface AvailableStoresResponse {
  name: string;
  street: string;
  zipCode: string;
  city: string;
  externalId: string;
}

export interface AvailableTimeSlot {
  id: string;
  from: string;
  to: string;
  timeSlotId: string;
}

export const NO_STORE_ID = 'default';
export const CONSOLIDATED_STORE_ID = 'all';
export const REMOTE_LOCATION_POSTAL_CODE = 'REMOTE';

export interface TimeSlotsCapacity {
  date: string;
  noOfTimeWindows: number;
}

export interface ConfirmAppointmentParams {
  zipCode: string;
  serviceId: string;
  storeId: string;
  timeSlotId: string;
  from: string;
  serviceCode: string;
  payload: Payload;
  selectedLanguage: string | null;
}
